import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import Cookies from 'js-cookie';
import { defaultTracyLatLng } from '../../../utils';
import {
  BASE_URL_SANDBOX,
  GET_CUSTOMER_ACCOUNT_INFO,
} from '../../../Constants/Config';
import eventEmitter from '../../../Components/Home/eventEmitter';
// Thunk: accepts authUserId (string|undefined|null)
export const fetchCustomerAddresses = createAsyncThunk(
  'customerAddress/fetchCustomerAddress',
  async (authUserId, { rejectWithValue }) => {
    try {
      let addresses = [];
      let userMeta = null;
      // Cookies.remove('userLocation');
      const setLocationFromAddress = (address) => {
        const coords = {
          lat: address.latitude || defaultTracyLatLng.lat,
          lng: address.longitude || defaultTracyLatLng.lng,
        };
        // Get previous location from cookies
        const prevCoords = Cookies.get('userLocation')
          ? JSON.parse(Cookies.get('userLocation'))
          : null;
          const isSameLocation =
          prevCoords &&
          prevCoords.lat === coords.lat &&
          prevCoords.lng === coords.lng;
      
          Cookies.set('userLocation', JSON.stringify(coords), { expires: 7 });
          if (!isSameLocation) {
            eventEmitter.emit('locationUpdated', coords);
          }
        if (address.zipCode) {
          Cookies.set('zipCode', JSON.stringify(address.zipCode), {
            expires: 7,
          });
        }
      };

      if (authUserId) {
        const response = await axios.post(
          BASE_URL_SANDBOX + GET_CUSTOMER_ACCOUNT_INFO,
          { id: authUserId, merchant_id: '' },
          { headers: { 'Content-Type': 'multipart/form-data' } },
        );

        if (response.status === 200) {
          addresses = Array.isArray(response.data?.address)
            ? response.data.address
            : [];
          userMeta = response.data?.message || null;

          // Check if user already has a location set (preserve current location)
          const currentLocation = Cookies.get('userLocation');
          
          // If default address exists but user already has a location, don't override it
          const defaultAddress = addresses.find((a) => a?.is_default === '1');

          if (defaultAddress && !currentLocation) {
            // Only set default address location if no current location exists
            setLocationFromAddress(defaultAddress);
            return { addresses, userMeta };
          } else if (currentLocation) {
            // Location cookie already present - no re-emit needed.
            // Initial mount of consumers fetches data; emitting here would
            // double-fetch every home widget on each auth-state refresh.
            return { addresses, userMeta };
          }
        }
      } else {
        // ✅ Anonymous user: Try cookie first
        const addressCookie = Cookies.get('customerAddresses');
        if (addressCookie) {
          try {
            const parsed = JSON.parse(addressCookie);
            addresses = Array.isArray(parsed) ? parsed : [];
            const defaultAddress = addresses.find((a) => a?.is_default === '1');
            if (defaultAddress) {
              setLocationFromAddress(defaultAddress);
              return { addresses, userMeta };
            }
          } catch (e) {}
        }
      }

      // No saved address → try browser geolocation
      if (authUserId && typeof navigator !== 'undefined' && navigator.geolocation) {
        try {
          const coords = await new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(
              (position) => {
                resolve({
                  lat: position.coords.latitude,
                  lng: position.coords.longitude,
                });
              },
              (error) => reject(error),
            );
          });

          Cookies.set('userLocation', JSON.stringify(coords), { expires: 7 });

          // Reverse geocode to get city and zip code
          try {
            const reverseGeoResponse = await fetch(
              `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${coords.lat}&longitude=${coords.lng}&localityLanguage=en`,
            );
            if (reverseGeoResponse.ok) {
              const geoData = await reverseGeoResponse.json();
              if (geoData.postcode) {
                Cookies.set('zipCode', JSON.stringify(geoData.postcode), {
                  expires: 7,
                });
              }
              const cityFromGeo =
                geoData.city ||
                geoData.locality ||
                geoData.principalSubdivision ||
                '';
              if (cityFromGeo) {
                Cookies.set('locationCity', cityFromGeo, { expires: 7 });
              }
            }
          } catch (reverseGeoError) {}

          eventEmitter.emit('locationUpdated', coords);
          return { addresses, userMeta };
        } catch (geoError) {}
      }

      // No saved location available. Do NOT force a full-page reload here -
      // it caused an infinite reload loop with the LandingPage redirect logic.
      // Consumers fall back to defaultTracyLatLng; the user can set a location
      // from the header at any time.
      return { addresses, userMeta };
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error.message ||
        'Failed to fetch addresses';
      return rejectWithValue(message);
    }
  },
);

const initialState = {
  loading: false,
  addresses: [],
  userMeta: null,
  error: null,
  defaultLocation: defaultTracyLatLng,
};

const slice = createSlice({
  name: 'customerAddress',
  initialState,
  reducers: {
    setAddress(state, action) {
      state.addresses = action.payload || [];
      state.error = null;
    },
    addAddress(state, action) {
      state.addresses.push(action.payload);
      state.error = null;
    },
    updateAddress(state, action) {
      const { id, updates } = action.payload;
      state.addresses = state.addresses.map((a) =>
        a.id === id ? { ...a, updates } : a,
      );
    },
    removeAddress(state, action) {
      state.addresses = state.addresses.filter((a) => a.id !== action.payload);
    },
    persistAddressesToCookie(state) {
      try {
        Cookies.set('customerAddresses', JSON.stringify(state.addresses), {
          expires: 7,
        });
      } catch (e) {}
    },
    clearAddresses(state) {
      state.addresses = [];
      state.userMeta = null;
      state.error = null;
      Cookies.remove('customerAddresses');
    },
    setDefaultLocation(state, action) {
      state.defaultLocation = action.payload;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(fetchCustomerAddresses.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCustomerAddresses.fulfilled, (state, action) => {
        state.loading = false;
        state.addresses = action.payload.addresses || [];
        state.userMeta = action.payload.userMeta || null;
      })
      .addCase(fetchCustomerAddresses.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload || action.error?.message || 'Unknown error';
        state.addresses = [];
      });
  },
});

export const {
  setAddress,
  addAddress,
  updateAddress,
  removeAddress,
  persistAddressesToCookie,
  clearAddresses,
  setDefaultLocation,
} = slice.actions;

export default slice.reducer;

export const selectCustomerAddresses = (state) =>
  state.customerAddress.addresses;
export const selectCustomerAddressLoading = (state) =>
  state.customerAddress.loading;
export const selectCustomerAddressError = (state) =>
  state.customerAddress.error;
export const selectCustomerUserMeta = (state) => state.customerAddress.userMeta;
export const selectDefaultLocation = (state) =>
  state.customerAddress.defaultLocation;
